import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import HotelDetailsType from '@customTypes/hotel-details';
import { getProductDetails } from '@utils/services';
import { ProductDetails } from '@customTypes/product-details';
import { useRouter } from 'next/router';
import { updateUrlQueryParam } from '@helpers/url';

export type HotelContextType = {
  date: Date | undefined;
  hotel: HotelDetailsType;
  handleProductCategoryChange: (category: string) => Promise<void>;
  handleSmartCalendarDateChange: (date: Date) => Promise<void>;
  initialProductCategory: string | null;
  productCategory: string | null;
  productDetails: ProductDetails | undefined;
  fetchProductDetails: (date?: Date) => Promise<void>;
  setDate: (date: Date) => void;
};

export const HotelContext = createContext<HotelContextType | undefined>(undefined);

export const useHotelContext = () => {
  const context = useContext(HotelContext);

  if (!context) {
    throw new Error('useHotelContext must be used within a HotelContext');
  }

  return context;
};

type HotelProviderProps = {
  children: React.ReactNode;
  value: {
    hotel: HotelDetailsType;
    initialProductCategory: string | null;
  };
};

export function HotelProvider({ children, value }: HotelProviderProps) {
  const router = useRouter();
  const { hotel, initialProductCategory } = value;

  const [date, setDate] = useState<Date | undefined>();
  const [productDetails, setProductDetails] = useState<ProductDetails | undefined>(undefined);
  const [productCategory, setProductCategory] = useState<string | null>(initialProductCategory);

  const fetchProductDetails = useCallback(
    async (d?: Date) => {
      try {
        const formattedDate = d ? dayjs(d).format('MM/DD/YYYY') : undefined;
        const details = await getProductDetails(hotel.id, formattedDate);
        setProductDetails(details);
      } catch (error) {
        console.error('Error fetching product details:', error);
      }
    },
    [hotel.id],
  );

  const handleProductCategoryChange = useCallback(
    async (category: string) => {
      await updateUrlQueryParam(router, 'product_category', category);
      setProductCategory(category);
    },
    [router],
  );

  const handleSmartCalendarDateChange = useCallback(
    async (d: Date) => {
      setDate(d);

      // Update URL with new date without causing a page reload
      const formattedDate = dayjs(d).format('MM-DD-YYYY');
      await updateUrlQueryParam(router, 'date', formattedDate);

      await fetchProductDetails(d);
    },
    [fetchProductDetails, router],
  );

  const contextValue = useMemo(
    () => ({
      date,
      fetchProductDetails,
      handleProductCategoryChange,
      handleSmartCalendarDateChange,
      hotel,
      initialProductCategory,
      productCategory,
      productDetails,
      setDate,
    }),
    [
      date,
      fetchProductDetails,
      handleProductCategoryChange,
      handleSmartCalendarDateChange,
      hotel,
      initialProductCategory,
      productCategory,
      productDetails,
      setDate,
    ],
  );

  return <HotelContext.Provider value={contextValue}>{children}</HotelContext.Provider>;
}
